/* UUIDv7 — time-ordered, client-generated, collision-safe offline.
 *
 * Why v7 and not v4: records are created on seven devices with no coordination
 * and arrive at the server out of order, sometimes days late. A sortable id
 * gives the projection rule a stable tiebreak (`max(changed_at, id)`) without
 * trusting any clock but the one that made the row.
 *
 * Layout (RFC 9562):
 *   48 bits  unix ms
 *    4 bits  version (7)
 *   12 bits  sub-ms counter — keeps same-millisecond ids monotonic
 *    2 bits  variant (0b10)
 *   62 bits  random
 */

let lastMs = 0;
let counter = 0;

export function uuidv7(): string {
  const ms = Date.now();

  if (ms === lastMs) {
    counter = (counter + 1) & 0xfff;
    // Overflowed 4096 ids inside one millisecond. Borrow from the next ms
    // rather than emit a non-monotonic id.
    if (counter === 0) lastMs = ms + 1;
  } else {
    lastMs = Math.max(ms, lastMs);
    counter = 0;
  }

  const bytes = new Uint8Array(16);
  const t = lastMs;

  bytes[0] = (t / 2 ** 40) & 0xff;
  bytes[1] = (t / 2 ** 32) & 0xff;
  bytes[2] = (t / 2 ** 24) & 0xff;
  bytes[3] = (t / 2 ** 16) & 0xff;
  bytes[4] = (t / 2 ** 8) & 0xff;
  bytes[5] = t & 0xff;

  bytes[6] = 0x70 | ((counter >> 8) & 0x0f);
  bytes[7] = counter & 0xff;

  const rand = new Uint8Array(8);
  crypto.getRandomValues(rand);
  bytes.set(rand, 8);
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push(bytes[i].toString(16).padStart(2, '0'));
  const s = hex.join('');
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}

/** Device clock. Geologic truth. Never replaced by a server value. */
export const nowIso = (): string => new Date().toISOString();
